import { Rocket } from 'lucide-react';
import { cn } from '@scio3/ui';
import type { AppMeta } from '@scio3/shared-types';

/**
 * The intended app's icon, for the logo tile on the pre-hook's TV.
 *
 * A deliberate ~15-line replica of `AppIconImage` in
 * `features/assets/components/dialogs/AppSelectorDialog.tsx` (the app picker's icon
 * renderer, and the source of truth for this fallback chain: `image` → `customIcon`
 * → `iconClass` → Rocket). That helper and its `APP_ICON_CDN` const are module-private
 * and AppSelectorDialog is outside this round's file set, so exporting them would widen
 * the blast radius further than copying the chain. If the fallback order changes there,
 * change it here too.
 *
 * Never hardcode a per-app asset: the pre-hook is generic and only the icon and the
 * interpolated name differ between apps.
 */

const APP_ICON_CDN = `${import.meta.env.VITE_CDN_URL || 'https://cdn.optisigns.com'}/scio/images/`;

interface PrehookAppIconProps {
  appMeta: AppMeta;
  /** Rendered edge length in px — the design's tile shows a 62px icon. */
  size: number;
}

export function PrehookAppIcon({ appMeta, size }: PrehookAppIconProps) {
  const alt = appMeta.text ?? '';
  const src = appMeta.image ?? appMeta.customIcon;

  if (src) {
    return (
      <img
        src={`${APP_ICON_CDN}${src}`}
        alt={alt}
        className="block object-contain"
        style={{ width: size, height: size }}
        // Deliberately NOT `loading="lazy"` (which AppSelectorDialog's grid rightly uses for
        // dozens of icons): this is the ONE image in a hero modal, and it is the only thing on
        // screen that says which app the visitor came for. Lazy-loading it left an empty white
        // tile for the opening beat of the fast path. `high` puts the ~32KB CDN fetch ahead of
        // whatever else the app shell is pulling at that moment.
        fetchPriority="high"
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center rounded-xl bg-primary/10"
      style={{ width: size, height: size }}
    >
      {appMeta.iconClass ? (
        <i className={cn(appMeta.iconClass, 'text-primary')} style={{ fontSize: size * 0.6 }} />
      ) : (
        <Rocket className="text-primary" style={{ width: size * 0.6, height: size * 0.6 }} />
      )}
    </div>
  );
}
